/**
 * "Which box am I on?" — the copy behind the environment banner (#1052).
 *
 * The sandbox and production are deliberately identical: same code, same
 * chrome, same shapes of data. That is what makes the sandbox useful and also
 * what makes it dangerous — two tabs both titled "Groundwork Command", one of
 * them the live business.
 *
 * The signal is the RUNTIME one. `GET /health` returns `sandbox: true` from
 * `SANDBOX_MODE`, which sandboxMode.ts never blanks, so it is truthful by
 * construction and reflects the server actually answering. The build-time
 * `VITE_ENV_LABEL` is not used: Vite inlines it at build time, so it depends on
 * a Docker build ARG reaching the image, and a safety marker that silently
 * fails to render is worse than none — it teaches people to trust its absence.
 */

export interface EnvironmentNotice {
    /** Short badge text. Kept to one word so it survives a 390px phone. */
    label: string;
    /** What it means, in the terms someone would act on. */
    detail: string;
}

/** The shape of the bit of `/health` this reads. */
export interface HealthLike {
    sandbox?: boolean;
}

/**
 * The banner to show, or null for "say nothing".
 *
 * Silence is the answer for production AND for an unreadable health response.
 * Claiming "sandbox" on production would be a lie; claiming nothing on the
 * sandbox is the status quo we're fixing, but a false alarm on the live
 * business during a network blip would train people to ignore the banner —
 * which costs more than the blip.
 */
export function environmentNotice(health: HealthLike | null | undefined): EnvironmentNotice | null {
    if (!health || health.sandbox !== true) return null;
    return {
        label: "SANDBOX",
        detail: "Practice copy — nothing here reaches a real customer.",
    };
}
